import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { ChatSettings, UpdateChatSettingsRequest } from '@nucleus-commerce/api-client/admin';

import { storeKeys } from '@/features/stores';

import { chatSettingsApi } from '../api/chat-settings-api';
import { mergeSavedSettings } from '../lib/settings-cache';

import { chatSettingsKeys } from './chat-settings-keys';

/**
 * Saves part of the store's chat settings. The backend locks the store row, so the save bar may
 * send several tabs at once; each answer is folded into the cache for the keys it sent only (see
 * `mergeSavedSettings`). The tab forms never re-read the cache, so another tab's unsaved edits stay.
 */
export function useUpdateChatSettings(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (patch: UpdateChatSettingsRequest) =>
      chatSettingsApi.updateSettings(storeId, patch),
    meta: { successMessage: 'chatSettings:toasts.saved', silentError: true },
    onSuccess: (saved, patch) => {
      queryClient.setQueryData<ChatSettings>(chatSettingsKeys.settings(storeId), (cached) =>
        mergeSavedSettings(cached, patch, saved),
      );
      // The store detail carries `settings.chat` too.
      void queryClient.invalidateQueries({ queryKey: storeKeys.detail(storeId) });
    },
  });
}
